import React, { useState, useCallback } from 'react';

import { useQuery } from '@tanstack/react-query';
import { FocusScope } from 'react-aria';

import {
  Modal,
  Icon,
  IconButton,
  ErrorText,
  LoadingSpinner,
  Pill,
  OutlineButton,
  Pagination,
  CardHeader,
  PlainButton,
} from '@components';
import ButtonBase from '@components/Button/ButtonBase';
import { HeadFootBlockFooter } from '@components/layout';
import { ListBox } from '@components/ListBox';
import { useOnKeyPress } from '@hooks';
import { deepCopy, getJson, getUserOS } from '@utils';

import { getName } from '../Sidebar/helpers';

import { mapObjects } from './fuzzySearchUtils';
import { SearchBar, Item } from './SearchBar';

const FuzzySearchBar = ({
  pageClientSlug,
  pageAccountSlug,
  clientList,
  accountsList,
}) => {
  const userOS = getUserOS();
  let tooltipText;
  if (userOS === 'Mac OS') {
    tooltipText = 'Cmd+/ to search';
  } else {
    tooltipText = 'Control+/ to search';
  }

  const [modalOpen, setModalOpen] = useState(false);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [modelName, setModelName] = useState<string>('');

  const clientName =
    pageClientSlug !== '' && getName(clientList, pageClientSlug);
  const accountName =
    pageAccountSlug !== '' && getName(accountsList, pageAccountSlug);

  const accountPrefix = `In Site`;
  const clientPrefix = `In Customer`;

  interface SearchOption {
    id: number;
    show: boolean;
    where: string;
    queryParams: {
      client_slug?: string;
      account_slug?: string;
      q?: string;
    };
  }

  // Different search options scenario based on clients and accounts list
  let searchOptions: SearchOption[] = [];

  if (clientList.length > 1) {
    searchOptions = [
      {
        id: 1,
        show: pageAccountSlug !== '',
        where: `${accountPrefix} ${accountName}`,
        queryParams: {
          client_slug: pageClientSlug,
          account_slug: pageAccountSlug,
        },
      },
      {
        id: 2,
        show: pageClientSlug !== '',
        where: `${clientPrefix} ${clientName}`,
        queryParams: {
          client_slug: pageClientSlug,
        },
      },
      {
        id: 3,
        show: clientList !== undefined && clientList.length > 1,
        where: 'In All SearchPilot',
        queryParams: {
          client_slug: '',
          account_slug: '',
        },
      },
    ];
  } else if (clientList.length === 1 && accountsList.length === 1) {
    const [account] = accountsList;

    searchOptions = [
      {
        id: 1,
        show: true,
        where: 'In All SearchPilot',
        queryParams: {
          client_slug: pageClientSlug,
          account_slug: account.slug,
        },
      },
    ];
  } else if (clientList.length === 1 && accountsList.length > 1) {
    searchOptions = [
      {
        id: 1,
        show: pageAccountSlug !== '',
        where: `${accountPrefix} ${accountName}`,
        queryParams: {
          client_slug: pageClientSlug,
          account_slug: pageAccountSlug,
        },
      },
      {
        id: 2,
        show: true,
        where: `All SearchPilot`,
        queryParams: {
          client_slug: pageClientSlug,
        },
      },
    ];
  }

  const [fieldState, setFieldState] = useState<{
    selectedKey: string;
    inputValue: string;
    items: SearchOption[];
    params: Record<string, string> | null;
    selectedItem: SearchOption | null | undefined;
  }>({
    selectedKey: '',
    inputValue: '',
    items: searchOptions,
    params: null,
    selectedItem: null,
  });

  //Shortcut keys to trigger search bar
  const keys = ['Meta', '/'];
  const handleKeyboardShortcut = useCallback(() => {
    setModalOpen((currentModalOpen) => !currentModalOpen);
    // clear search state when meta + / keys pressed
    setFieldState({
      selectedKey: '',
      inputValue: '',
      items: searchOptions,
      params: null,
      selectedItem: null,
    });
  }, [setModalOpen, setFieldState]);

  useOnKeyPress(keys, handleKeyboardShortcut);

  // Specify how each of the values should change when an option is selected from the list box
  const onSelectionChange = (key) => {
    setFieldState((prevState) => {
      const selectedItem = prevState.items.find((option) => option.id === key);

      return {
        inputValue: prevState.inputValue ?? '',
        selectedKey: key === '' ? prevState.items[0].id : key,
        items: searchOptions.filter((item) => item.show === true),
        params: selectedItem
          ? { ...selectedItem.queryParams, q: fieldState.inputValue }
          : { ...prevState.items[0].queryParams, q: fieldState.inputValue },
        selectedItem:
          prevState.selectedKey !== '' ? selectedItem : prevState.items[0],
      };
    });
  };

  // Specify how each of the ComboBox values should change when the input
  // field is altered by the user
  const onInputChange = (value) => {
    setFieldState((prevState) => {
      return {
        inputValue: value,
        selectedKey: prevState.selectedKey,
        items: searchOptions.filter((item) => item.show === true),
        params: prevState.params,
        selectedItem:
          prevState.selectedKey !== ''
            ? searchOptions.find(
                (item) => Number(item.id) === Number(prevState.selectedKey),
              )
            : prevState.items[0],
      };
    });
  };

  // Show entire list if user opens the menu manually
  const onOpenChange = (isOpen, menuTrigger) => {
    if (menuTrigger === 'manual' && isOpen) {
      setFieldState((prevState) => {
        const selectedItem = searchOptions.find(
          (item) => Number(item.id) === Number(prevState.selectedKey),
        );
        return {
          inputValue: prevState.inputValue,
          selectedKey: prevState.selectedKey,
          items: searchOptions.filter((item) => item.show === true),
          params: prevState.params,
          selectedItem: selectedItem,
        };
      });
    }
  };

  const url = '/api/webapp/search/';
  const fetchSearchQueryResults = async (
    queryParams,
    limit,
    offset,
    modelName,
  ) => {
    const tmpParams = deepCopy(queryParams);
    tmpParams['offset'] = offset;
    tmpParams['limit'] = limit;
    tmpParams['model_name'] = modelName;

    return await getJson(url, tmpParams);
  };

  //Fetch data with params on trigger
  const { isPending, error, data } = useQuery({
    queryKey: [
      'fuzzySearchQueryResults',
      fieldState.params,
      offset,
      limit,
      modelName,
    ],
    queryFn: async () => {
      return await fetchSearchQueryResults(
        fieldState.params,
        limit,
        offset,
        modelName,
      );
    },
    enabled: !!fieldState.params,
  });

  const handleClose = () => {
    setModalOpen(false);
    setFieldState({
      selectedKey: '',
      inputValue: '',
      items: searchOptions,
      params: null,
      selectedItem: null,
    });
    setModelName('');
  };

  return (
    <>
      <IconButton.Protected
        iconName="search"
        onClick={() => setModalOpen(true)}
        tooltipText={tooltipText}
        tooltipPosition="bottom"
      />
      <Modal
        open={modalOpen}
        onClose={() => handleClose()}
        defaultWidth={'min-w-5xl'}
        maxWidth={'w-2/3'}
      >
        <CardHeader backgroundColor="bg-protected-gradient">
          <div className="flex items-center gap-2">Search</div>
          <OutlineButton
            size="sm"
            variant="primary"
            onClick={() => handleClose()}
          >
            Esc
          </OutlineButton>
        </CardHeader>
        <div className="px-5 py-3">
          <div className="flex justify-between gap-4">
            <SearchBar
              autoFocus
              items={fieldState.items}
              selectedKey={fieldState.selectedKey}
              inputValue={fieldState.inputValue}
              onOpenChange={onOpenChange}
              onSelectionChange={(key) => {
                if (fieldState.inputValue !== '') {
                  onSelectionChange(key);
                }
              }}
              onInputChange={onInputChange}
              aria-label="Search bar"
            >
              {(item) => (
                <Item textValue={`${fieldState.inputValue}`}>
                  <div className="flex w-full flex-row items-center justify-between">
                    <div className="flex grow flex-row items-center">
                      <Icon name="search" aria-hidden="true" />{' '}
                      <p className="mx-2 break-normal">
                        {fieldState.inputValue}
                      </p>
                    </div>
                    <div className="bg-sp-neutral-200 text-sp-neutral flex rounded-md p-1 text-xs">
                      {item.where} <Icon name="AiOutlineEnter" />
                    </div>
                  </div>
                </Item>
              )}
            </SearchBar>
            <ButtonBase
              onClick={() => {
                setIsFilterOpen((prevState) => !prevState);
                setModelName('');
              }}
              className={
                isFilterOpen
                  ? 'sp-btn-action shadow-xs flex-1 border'
                  : 'shadow-xs sp-btn-action-outline border-sp-primary text-sp-primary-600 flex-1 border'
              }
              leftIcon={
                <Icon
                  name="filter"
                  size="sm"
                  color={isFilterOpen ? 'white' : 'text-sp-primary-600'}
                />
              }
            >
              Filters
            </ButtonBase>
          </div>
          {isFilterOpen && (
            <>
              <div className="mt-2 flex gap-1">
                {Object.keys(mapObjects).map((objectKey) => (
                  <ButtonBase
                    key={objectKey}
                    className={`bg-sp-neutral-100 py-1 ${modelName === objectKey ? 'sp-btn-action bg-sp-primary-600' : ''}`}
                    onClick={() => setModelName(objectKey)}
                  >
                    {mapObjects[objectKey]}
                  </ButtonBase>
                ))}
              </div>
              <PlainButton
                variant="primary"
                size="sm"
                onClick={() => setModelName('')}
                leftIcon={<Icon name="closeCross" size="sm" />}
              >
                Clear Filter
              </PlainButton>
            </>
          )}
        </div>

        {isPending ? (
          <div className="my-2 p-4 text-center">
            <p>Searching...</p>
            <LoadingSpinner />
          </div>
        ) : null}

        {data && data.results.length !== 0 ? (
          <FocusScope autoFocus>
            <div className="rounded-b-lg p-4">
              <h4 className="text-base font-semibold">
                <span className="mr-2">
                  Results ({data.results.length} of {data.count}){' '}
                </span>
                <Pill
                  text={`"${fieldState.inputValue}": ${fieldState.selectedItem?.where}`}
                  colour={'purple'}
                />
              </h4>

              <ListBox
                label="Alignment"
                selectionMode="single"
                items={data.results}
                className="min-w-72"
              >
                {(item) => (
                  <Item textValue={item.title} key={item.link}>
                    <a className="text-sp-neutral w-full" href={item.link}>
                      <span className="my-2 flex flex-col">
                        <p className="text-base font-semibold">
                          {mapObjects[item.model_name]}: {item.title}
                        </p>
                        <p className="text-sp-neutral-500 text-sm font-medium">
                          {item.description}
                        </p>
                      </span>
                    </a>
                  </Item>
                )}
              </ListBox>
            </div>
          </FocusScope>
        ) : null}

        {data && data.results.length === 0 && (
          <div className="my-2 flex items-center justify-center p-4 text-center">
            <p className="mr-2">No results for</p>
            <Pill
              text={`"${fieldState.inputValue}": ${fieldState.selectedItem?.where}`}
              colour={'purple'}
            />
          </div>
        )}

        {error && (
          <span className="my-2 p-4 text-center">
            <ErrorText text="Uh oh, Something went wrong" />
          </span>
        )}
        <HeadFootBlockFooter>
          <Pagination
            totalCount={data?.count || 0}
            listItems={data?.results || []}
            setLimit={setLimit}
            setOffset={setOffset}
            offset={offset}
            limit={limit}
            hideShowingTotalCount={true}
          />
        </HeadFootBlockFooter>
      </Modal>
    </>
  );
};

export default FuzzySearchBar;
